import React, { useState, useEffect } from "react";
import {
  Avatar,
  Grid,
  Box,
  ButtonBase,
  Typography,
  IconButton,
  useTheme,
  Stack,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DropZoneIcon from "../../../../assets/icons1/dropzone_icon.svg";
import defaultAvatar from "../../../../assets/icons1/avatars/default.png";
import male1 from "../../../../assets/icons1/avatars/male1.png";
import male2 from "../../../../assets/icons1/avatars/male2.png";
import male3 from "../../../../assets/icons1/avatars/male3.png";
import male4 from "../../../../assets/icons1/avatars/male4.png";
import female1 from "../../../../assets/icons1/avatars/female1.png";
import female2 from "../../../../assets/icons1/avatars/female2.png";
import { openErrorNotification } from "helpers/methods";
import { AppModal, ModalSize } from "global/components/AppModal";
import { focusRing } from "theme/colors";

const avatarList = [
  defaultAvatar,
  male1,
  male2,
  male3,
  male4,
  female1,
  female2,
];

interface ProfileImageModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (img: string) => void;
  initialImage?: string;
  /** Modal System width step. */
  maxWidth?: ModalSize;
}

const ProfileImageModal: React.FC<ProfileImageModalProps> = ({
  open,
  onClose,
  onSave,
  initialImage,
  maxWidth = "md",
}) => {
  const theme = useTheme();
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const allowedExtensions = [".jpeg", ".jpg", ".png", ".gif", ".bmp", ".tiff"];

  useEffect(() => {
    if (open) {
      setSelectedAvatar(initialImage || null);
      setUploadedImage(null);
    }
  }, [open, initialImage]);

  const handleAvatarClick = (img: string) => {
    setSelectedAvatar(img);
    setUploadedImage(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
    if (!allowedExtensions.includes(fileExtension)) {
      openErrorNotification(
        `File ${file.name} has an unsupported file format.`
      );
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedImage(reader.result as string);
      setSelectedAvatar(null);
    };
    reader.readAsDataURL(file);
  };
  const handleRemoveUpload = () => setUploadedImage(null);

  const handleSave = () => {
    if (uploadedImage) onSave(uploadedImage);
    else if (selectedAvatar) onSave(selectedAvatar);
    onClose();
  };

  const openUploadDialog = () =>
    document.getElementById("profile-upload-input")?.click();

  /** Shared chrome for the circular upload target (both states). */
  const dropzoneSx = {
    width: 120,
    height: 120,
    borderRadius: "50%",
    border: `1px dashed ${theme.palette.grey[400]}`,
    mx: "auto",
    position: "relative",
    bgcolor: uploadedImage ? "#E1EAE1" : "#F5F5F5",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: "2px",
    },
  } as const;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Select Profile Image"
      size={maxWidth}
      primaryLabel="Save"
      onPrimary={handleSave}
      primaryDisabled={!selectedAvatar && !uploadedImage}
      cancelLabel="Cancel"
      onCancel={onClose}
    >
      <Stack spacing={3}>
          <Box>
            <Typography
              component="h3"
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Choose an Avatar
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {avatarList.map((img, idx) => {
                const isSelected = selectedAvatar === img;
                return (
                  <Grid item key={idx}>
                    <ButtonBase
                      disableRipple
                      aria-label={`Avatar option ${idx + 1}`}
                      aria-pressed={isSelected}
                      sx={{
                        position: "relative",
                        width: 76,
                        height: 76,
                        borderRadius: "50%",
                        overflow: "visible",
                        transition: "all 0.2s ease-in-out",
                        boxShadow: isSelected
                          ? `0 0 0 3px ${theme.palette.primary.main}`
                          : "0 0 0 2px #e0e0e0",
                        backgroundColor: isSelected
                          ? theme.palette.action.hover
                          : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: `0 0 0 3px ${theme.palette.primary.light}`,
                        },
                        "&.Mui-focusVisible": {
                          outline: "2px solid",
                          outlineColor: "primary.main",
                          outlineOffset: "4px",
                        },
                      }}
                      onClick={() => {
                        handleAvatarClick(img);
                      }}
                    >
                      <Avatar
                        src={img}
                        alt=""
                        sx={{
                          width: 68,
                          height: 68,
                          transition: "transform 0.2s ease",
                          transform: isSelected ? "scale(1.05)" : "scale(1)",
                        }}
                      />
                      {isSelected && (
                        <CheckCircleIcon
                          aria-hidden="true"
                          sx={{
                            position: "absolute",
                            bottom: -1,
                            right: -1,
                            fontSize: 20,
                            color: theme.palette.primary.main,
                            backgroundColor: "#fff",
                            borderRadius: "50%",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                          }}
                        />
                      )}
                    </ButtonBase>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          <Box sx={{ textAlign: "center", color: theme.palette.text.disabled }}>
            — or —
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography
              component="h3"
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Upload Your Profile
            </Typography>

            {uploadedImage ? (
              <Box
                sx={{
                  ...dropzoneSx,
                  "&:focus-visible": {
                    outline: `2px solid ${focusRing}`,
                    outlineOffset: "2px",
                  },
                }}
                role="button"
                tabIndex={0}
                aria-label="Change profile photo"
                onClick={openUploadDialog}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openUploadDialog();
                  }
                }}
              >
                <Avatar
                  src={uploadedImage}
                  alt="Uploaded profile image preview"
                  sx={{
                    width: 115,
                    height: 115,
                    borderRadius: "50%",
                    boxShadow: 1,
                    zIndex: 1,
                  }}
                />
                <IconButton
                  size="small"
                  aria-label="Remove uploaded image"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveUpload();
                  }}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: -2,
                    zIndex: 2,
                    bgcolor: "#fff",
                    border: `1px solid ${theme.palette.primary.main}`,
                    color: `${theme.palette.primary.main}`,
                    p: 0.5,
                    "&:hover": { bgcolor: "#f4f4f4" },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <ButtonBase
                disableRipple
                aria-label="Upload profile image"
                onClick={openUploadDialog}
                sx={dropzoneSx}
              >
                <img
                  src={DropZoneIcon}
                  alt=""
                  style={{ width: 40, height: 40, opacity: 0.9 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    fontWeight: 500,
                    fontSize: 12,
                    color: "#646464",
                  }}
                >
                  Click Here To Upload
                </Typography>
              </ButtonBase>
            )}

            <input
              id="profile-upload-input"
              type="file"
              accept={allowedExtensions.join(",")}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Box>
      </Stack>
    </AppModal>
  );
};

export default ProfileImageModal;
